/**
 * declarationStatus — единственото място, което превежда `AssignmentStatus` в знак.
 *
 * Защо съществува: до 09.09.2026 четирите състояния на една декларация се
 * свиваха до ДВЕ по целия път — сървърът филтрираше `status == 'signed'`,
 * типът носеше `hasX: boolean`, а екранът рисуваше `✓` или `✗`. Тоест
 * „родителят ОТКАЗА" изглеждаше точно като „още не е подал" и никой не търсеше
 * проблем. Реален случай: родител отхвърли пълномощното докато го попълваше и
 * се чакаше напразно.
 *
 * 🔴 Таблицата е `Record<AssignmentStatus, …>`, а НЕ обект с преписани ключове:
 * добави ли се пето състояние в `AssignmentStatus`, ТУК спира компилацията.
 * Списъкът със състояния се ИЗВЕЖДА оттук (`Object.keys`), не се преписва —
 * преписан списък мълчи точно когато типът се промени.
 */

import type { AssignmentStatus } from '../types';

/** Вариантите на `Badge` (`frontend/src/components/ui/Badge.tsx`). */
export type DeclarationStatusVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface DeclarationStatusDisplay {
  /** Знакът в клетката. */
  sign: string;
  /** Цветът на `Badge`-а. */
  variant: DeclarationStatusVariant;
  /**
   * Иска ли това състояние ДЕЙСТВИЕ от админ/учител.
   *
   * `pending` НЕ иска — то е нормалният ход на нещата. `declined` иска: родителят
   * е отказал и без намеса нищо повече няма да се случи.
   */
  needsAttention: boolean;
}

export const DECLARATION_STATUS_DISPLAY: Record<AssignmentStatus, DeclarationStatusDisplay> = {
  signed: { sign: '✓', variant: 'success', needsAttention: false },
  pending: { sign: '⏳', variant: 'info', needsAttention: false },
  declined: { sign: '✗', variant: 'danger', needsAttention: true },
  revoked: { sign: '⊘', variant: 'neutral', needsAttention: true },
};

/**
 * Всички състояния — ИЗВЕДЕНИ от таблицата отгоре, не преписани.
 * Тестовете обхождат този списък, за да не проверяват само тези, които са им хрумнали.
 */
export const ASSIGNMENT_STATUSES = Object.keys(
  DECLARATION_STATUS_DISPLAY,
) as AssignmentStatus[];

/** Знакът за клетка БЕЗ възложена декларация — това не е състояние, а липса. */
export const NOT_ASSIGNED_SIGN = '—';

/**
 * Знакът за едно състояние. `undefined` (декларацията не е възложена) дава тире,
 * а не `✗` — „не е искано от този родител" и „родителят не го е дал" са различни
 * неща и админът трябва да ги различава.
 */
export function declarationStatusDisplay(
  status: AssignmentStatus | undefined | null,
): DeclarationStatusDisplay | null {
  if (!status) return null;
  return DECLARATION_STATUS_DISPLAY[status] ?? null;
}

// ============================================================================
// КОЕ ВЪЗЛАГАНЕ Е „ТЕКУЩОТО" ЗА ЕДИН КОД
// ============================================================================

/** Минимумът от `Assignment`, нужен за избора. Приема и raw Firestore документ. */
export interface AssignmentForStatus {
  assignmentId: string;
  declarationCode: string;
  status: AssignmentStatus;
  signatureId?: string | null;
  /**
   * `metadata.lastStatusChangeAt` в милисекунди. Липсва при заварени документи —
   * тогава се брои за 0, тоест по-нов запис винаги го побеждава.
   */
  lastStatusChangeMillis?: number | null;
}

/**
 * Текущото възлагане за всеки декларационен код.
 *
 * Едно дете може да има ПОВЕЧЕ от едно възлагане за същия код — по едно на
 * учебна година. Докато сървърът филтрираше по `status == 'signed'`, въпросът не
 * стоеше: питаше се само „има ли изобщо подписано". Щом четем и четирите
 * състояния, изборът трябва да е ДЕТЕРМИНИРАН, иначе екранът мига между
 * миналогодишното „подписано" и тазгодишното „чака".
 *
 * ⚠️ Изборът НЕ е по учебна година нарочно: годината се пази в базата с два
 * правописа (`2026/2027` и `2026-2027`) и филтър по нея връща тиха нула, която
 * изглежда като факт. Времето на последната смяна е налично и в двата случая.
 *
 * Ред: най-скорошна смяна на статуса печели; при равенство — по-големият
 * `assignmentId` (лексикографски), за да не трепти резултатът между две четения.
 */
export function pickCurrentAssignmentPerCode<T extends AssignmentForStatus>(
  assignments: T[],
): Map<string, T> {
  const current = new Map<string, T>();
  for (const a of assignments) {
    if (!a || !a.declarationCode) continue;
    const seen = current.get(a.declarationCode);
    if (!seen || isNewerAssignment(a, seen)) {
      current.set(a.declarationCode, a);
    }
  }
  return current;
}

function isNewerAssignment(a: AssignmentForStatus, b: AssignmentForStatus): boolean {
  const ma = a.lastStatusChangeMillis ?? 0;
  const mb = b.lastStatusChangeMillis ?? 0;
  if (ma !== mb) return ma > mb;
  return (a.assignmentId || '') > (b.assignmentId || '');
}
